<template>
  <v-container fluid class="px-4 pt-0">
    <v-row>
      <v-col v-if="businessRulesGetBoolean('Inventory Classes')" cols="2" class="pb-0">
        <inventory-class-select-element v-model="inventoryClassFilterId" :hideDetails="true"/>
      </v-col>

      <v-col cols="2">
        <v-select label="Category" v-model="categoryFilterId" hide-details/>
      </v-col>

      <v-col cols="2">
        <v-autocomplete
          :label="businessRulesGetBoolean('Jobs Rename to Release') ? 'Release' : 'Job'"
          v-model="jobFilterId"
          :items="jobs"
          hide-details
          item-value="id"
          :item-text="item => `${item.code} - ${item.name}; ${item.reference}`"
          clearable
          :loading="loadingJobs"
        >
          <template #selection="{ item }">
            <span>
              <span style="font-weight: bold;">{{ item.code ? `${item.code}` : '' }}</span> - {{ item.name ? item.name : '' }} <span style="color: #1976D2;">{{ item.reference ? `; ${item.reference}` : '' }}</span>
            </span>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="2">
        <v-select label="Status" hide-details v-model="statusFilterId"/>
      </v-col>

      <v-col cols="4" class="text-right pt-7">
        <v-btn color="primary" outlined @click="openInitForm">
          <v-icon small class="mr-1">
            fas fa-plus
          </v-icon>
          New Allocation
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="tableHeaders"
          :items="allocations"
          :footer-props="footer"
          :items-per-page="pagination.itemsPerPage"
          :search="search"
          :loading="tableIsLoading"
          dense
          fixed-header
          :server-items-length="numberOfAllocations"
        >
          <template #item="{ item }">
            <tr :style="{ 'background-color': getRowBackgroundColor(item) }">
              <td>{{ item.id }}</td>
              <td>
                <v-chip small text-color="white" :color="item.job.class.color">
                  {{ item.job.class.name }}
                </v-chip>
              </td>
              <td>
                <span v-if="item.job">  
                  <span style="font-weight: bold;">
                    <text-highlight :queries="search">{{ item.job.code ? `${item.job.code}` : '' }}</text-highlight>
                  </span>
                  - 
                  <text-highlight :queries="search">{{ item.job.project ? item.job.project.name : '' }}</text-highlight>
                  <span style="color: #1976D2;">
                    <text-highlight :queries="search">{{ item.job.reference ? `; ${item.job.reference}` : '' }}</text-highlight>
                  </span>
                </span>
              </td>
              <td>{{ item.qty_required }}</td>
              <td>{{ item.qty_allocated }}</td>
              <td>{{ item.qty_supplied }}</td>
              <td>{{ item.qty_pre_allocated }}</td>
              <td>{{ item.allocate_by }}</td>
              <td>{{ formatPercent(item.qty_allocated / item.qty_required) }}</td>
              <td>{{ formatPercent(item.qty_pre_allocated / item.qty_required) }}</td>
              <td>{{ item.user.first_name }} {{ item.user.last_name }}</td>
              <td>{{ item.created_at }}</td>
              <td class="text-center">
                <div v-if="item.status_id in statusMap">
                  <v-chip small :text-color="statusMap[item.status_id].text_color" :color="statusMap[item.status_id].color">
                    {{ statusMap[item.status_id].name }}
                  </v-chip>
                </div>
              </td>
              <td class="text-right">
                <v-menu offset-y>
                  <template #activator="{ on, attrs }">
                    <v-btn class="ml-1 my-1" elevation="0" fab outlined x-small v-bind="attrs" tile color="primary" style="border-radius: 7px; width: 28px !important; height: 28px !important;" v-on="on">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list class="pa-3">

                    <v-list-item @click="openAllocationForm(item.id)">
                      <v-list-item-title style="font-weight: 600;">
                        OPEN
                      </v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="issueAllocation(item.id)">
                      <v-list-item-title :style="`color: ${item.is_issued ? 'rgba(0,0,0,0.26)' : 'green'}; font-weight: 600;`">
                        ISSUE
                      </v-list-item-title>
                    </v-list-item>

                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog v-model="showAllocationInitForm" max-width="33%" persistent>
      <inventory-allocation-init-form 
        @allocation-initialized="onAllocationInitialized"
        @close="showAllocationInitForm = false"
      />
    </v-dialog>

    <v-dialog v-model="showAllocationForm" fullscreen persistent>
      <inventory-allocation-form
        ref="allocationForm"
        @close="showAllocationForm = false"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import InventoryClassSelectElement from '../components/elements/InventoryClassSelectElement.vue';
import InventoryAllocationInitForm from '../components/forms/inventoryAllocation/InventoryAllocationInitForm.vue';
import InventoryAllocationForm from '../components/forms/inventoryAllocation/InventoryAllocationForm.vue';
import * as allocationApi from '../api/InventoryAllocationApi';

export default {
  components: {
    InventoryClassSelectElement,
    InventoryAllocationInitForm,
    InventoryAllocationForm
  },
  data: () => ({
    showAllocationInitForm: false,
    showAllocationForm: false,
    inventoryClassFilterId: null,
    categoryFilterId: null,
    jobFilterId: null,
    statusFilterId: null,
    dateCreatedFilter: null,
    jobs: [],
    loadingJobs: false,
    footer: {
      'items-per-page-options': [20, 50, 100, 500, 1000],
    },
    pagination: {
      itemsPerPage: 25.
    },
    tableIsLoading: false,
    allocations: [],
    numberOfAllocations: 0,
    totalTableRows: 5,
    tableHeaders: [
      { text: 'ID', value: 'id' },
      { text: 'Class', value: 'job_id' },
      { text: 'Release', value: 'job_id' },
      { text: 'QTY Required', value: 'qty_required' },
      { text: 'QTY Allocated', value: 'qty_allocated' },
      { text: 'QTY Supplied', value: 'qty_supplied' },
      { text: 'QTY Pre-Alloc.', value: 'qty_pre_allocated' },
      { text: 'Allocate By', value: 'allocate_by' },
      { text: 'Allocation %', value: 'id' },
      { text: 'Pre-Alloc %', value: 'id' },
      { text: 'Created By', value: 'created_by' },
      { text: 'Created At', value: 'created_at' },
      { text: 'Status', value: 'id', align: 'center' },
      { text: 'Actions', value: 'id' },
    ],
    pagination: {
      page: 1,
      itemsPerPage: 25
    },
    sort: {
      sortBy: 'id',
      sortDirection: 'desc'
    },
    statusMap: {},
  }),
  computed: {
    ...mapState({
      search: (state) => state.search,
      user: (state) => state.user,
  }),
    ...mapGetters([
      'businessRulesGetBoolean',
      'getAdminModuleValue',
    ]),
    isAuthorized() {
      return this.user.roles.some(x => x.name === 'Allocation Admin' || x.name === 'Admin');
    }
  },
  watch: {
    pagination() {
      if (this.isInitialized) this.loadTableData();
    }, 
    sort() {
      if (this.isInitialized) this.loadTableData();
    },
    search() {
      this.loadTableData();
    },
    inventoryClassFilterId() {
      this.loadTableData();
    },
    jobFilterId() {
      this.loadTableData();
    },
    statusFilterId() {
      this.loadTableData();
    }
  },
  created() {
    if (!this.isAuthorized) this.$router.push('/');
    this.init();
  },
  methods: {
    async init() {
      Promise.all([
        this.$api.job.readDDL(),
        allocationApi.getStatuses()
      ]).then(([jobs, statuses]) => {
        this.jobs = jobs;
        this.statusMap = statuses.reduce((acc, x) => {
          acc[x.id] = x;
          return acc;
        }, {});

        this.loadTableData();
      });
    },

    async loadTableData() {
      this.tableIsLoading = true;
      try {
        const data = {
          filters: {
            inventory_class_id: this.inventoryClassFilterId,
            job_id: this.jobFilterId,
            status_id: this.statusFilterId,
            date_created: this.dateCreatedFilter,
            category_id: this.categoryFilterId,
          },
          pagination: this.pagination,
          sort: this.sort,
          search: this.search
        };
        const res = await allocationApi.readList(data);
        this.allocations = res.data;
        this.numberOfAllocations = res.total;
      } catch (e) {
        console.error(e);
        this.$bus.$emit('showError', 'There was an error loading table data');
      } finally {
        this.tableIsLoading = false;
      }
    },

    openInitForm() {
      this.showAllocationInitForm = true;
    },

    onAllocationInitialized(allocationId) {
      this.showAllocationInitForm = false;
      this.openAllocationForm(allocationId);
    },

    openAllocationForm(allocationId) {
      this.showAllocationForm = true;
      setTimeout(() => this.$refs.allocationForm.open(allocationId), 250);
    },

    getRowBackgroundColor(row) {
      const allocationPercent = 100 * row.qty_allocated  / row.qty_required;
      if (row.allocate_by !== null) {
        if (allocationPercent <= 20) return 'red';
        if (allocationPercent < 90) return 'orange';
      }
      return 'auto';
    },

    formatPercent(value, isDecimal = true) {
      if (value === null || value === undefined || isNaN(value)) return '-';
      if (typeof(value) === 'string') value = Number(value);
      if (isDecimal) value *= 100;
      return `${Math.round(value)}%`;
    },

    async issueAllocation(allocationId) {
      try {
        const res = await allocationApi.issueAllocation(allocationId);
        if (res !== true) throw new Error();
        this.$bus.$emit('showSuccess', 'The allocation has been issued');
        this.loadTableData();
      } catch (e) {
        console.error(e);
        this.$bus.$emit('showError', e.response?.data || `Failed to Issue Allocation.`);
      }
    },
  },
};
</script>